import olMap from 'ol/Map';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import Style from 'ol/style/Style';
import * as olProj from 'ol/proj';
import { MapBrowserEvent } from 'ol';
import { GpxRouteApi, IGpxWaypoint, IGpxFilterCriteria } from './GpxRouteApi';
import { GpxRouteStore } from './GpxRouteStore';
import { GpxIconFactory } from './GpxIconFactory';
import { GpxLayerZIndex } from './GpxLayerOrder';

/** Minimum zoom before waypoint markers appear (unless their route is being edited). */
const WAYPOINT_MIN_ZOOM = 11.5;

/**
 * The waypoint marker layer. Outside an edit session it shows the waypoints
 * of every visible route (API-fed, zoom-gated). During an edit session it
 * shows only the editing route's waypoints, including unsaved drafts.
 */
export class GpxWaypointLayer {

    private source: VectorSource = new VectorSource();
    private layer: VectorLayer;
    private editingGpxGuid: string | null = null;

    public constructor(
        private map: olMap,
        private api: GpxRouteApi,
        private store: GpxRouteStore,
        private iconFactory: GpxIconFactory,
        private getCriteria: () => IGpxFilterCriteria
    ) {
        this.layer = new VectorLayer({
            source: this.source,
            minZoom: WAYPOINT_MIN_ZOOM,
            style: this.styleFunction,
            zIndex: GpxLayerZIndex.waypoints
        });
        this.layer.set('id', 'gpx-waypoints', true);
        this.map.addLayer(this.layer);

        void this.refresh();
    }

    get olLayer(): VectorLayer {
        return this.layer;
    }

    /**
     * Force the style function to re-evaluate (e.g. after a route's visibility
     * changed). Without this the markers keep their last render until something
     * else redraws the layer, such as the user panning the map.
     */
    public invalidateStyles = (): void => {
        this.layer.changed();
    }

    /** Reloads all waypoints from the API (call after routes/waypoints change). */
    public refresh = async (): Promise<void> => {
        if (this.editingGpxGuid !== null) {
            //an edit session owns the layer content — skip background refreshes
            return;
        }
        try {
            // Match the routes layer: only waypoints of routes passing the active filter.
            const waypoints = await this.api.getWaypoints(false, this.getCriteria());
            this.replaceAll(waypoints);
        } catch (error) {
            console.error('Failed to load GPX waypoints', error);
        }
    }

    /**
     * Enters/leaves editing display mode. While editing, only the editing
     * route's waypoints render and the zoom gate is lifted.
     */
    public setEditingRoute = (gpxGuid: string | null): void => {
        this.editingGpxGuid = gpxGuid;
        this.layer.setMinZoom(gpxGuid !== null ? 0 : WAYPOINT_MIN_ZOOM);
        this.layer.changed();
        if (gpxGuid === null) {
            void this.refresh();
        }
    }

    /** Replaces the features for one route (used when an edit session loads/saves). */
    public setRouteWaypoints = (gpxGuid: string, waypoints: IGpxWaypoint[]): void => {
        for (const feature of this.getRouteFeatures(gpxGuid)) {
            this.source.removeFeature(feature);
        }
        for (const waypoint of waypoints) {
            this.source.addFeature(this.toFeature(waypoint));
        }
    }

    public getRouteFeatures = (gpxGuid: string): Feature[] => {
        return this.source.getFeatures().filter(f => f.get('gpxGuid') === gpxGuid);
    }

    public getFeatureByGuid = (waypointGuid: string): Feature | undefined => {
        return this.source.getFeatures().find(f => f.get('waypointGuid') === waypointGuid);
    }

    public addFeature = (feature: Feature): void => {
        this.source.addFeature(feature);
    }

    public removeFeature = (feature: Feature): void => {
        this.source.removeFeature(feature);
    }

    public toFeature = (waypoint: IGpxWaypoint): Feature => {
        const feature = new Feature({
            geometry: new Point(olProj.fromLonLat([waypoint.lon, waypoint.lat]))
        });
        feature.set('waypointGuid', waypoint.waypointGuid);
        feature.set('gpxGuid', waypoint.gpxGuid);
        feature.set('waypointTypeId', waypoint.waypointTypeId);
        feature.set('name', waypoint.name);
        feature.set('description', waypoint.description ?? null);
        return feature;
    }

    /** Hit-test for a waypoint marker at the event pixel. */
    public findWaypointAtPixel = (evt: MapBrowserEvent): Feature | null => {
        let found: Feature | null = null;
        this.map.forEachFeatureAtPixel(evt.pixel, (feature) => {
            found = <Feature>feature;
            return true;
        }, {
            layerFilter: (layer) => layer === this.layer,
            hitTolerance: 5
        });
        return found;
    }

    private replaceAll(waypoints: IGpxWaypoint[]): void {
        this.source.clear();
        for (const waypoint of waypoints) {
            this.source.addFeature(this.toFeature(waypoint));
        }
    }

    private styleFunction = (feature: Feature): Style | Style[] => {
        const gpxGuid = <string>feature.get('gpxGuid');

        if (this.editingGpxGuid !== null) {
            //editing: only the session's waypoints are visible
            if (gpxGuid !== this.editingGpxGuid) {
                return [];
            }
        } else {
            //normal display: hide waypoints of hidden routes
            const record = this.store.get(gpxGuid);
            if (record && record.isVisible === false) {
                return [];
            }
        }

        const style = this.iconFactory.getStyle(<number>feature.get('waypointTypeId'));
        return style ?? [];
    }
}
